// FileUpload.jsx
import React, { useState } from 'react';
import {
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Box,
	IconButton,
	CircularProgress,
	Typography,
	Button,
	useTheme,
} from '@mui/material';
import '@/styles/fileuploader.scss';
import { FileUploader } from 'react-drag-drop-files';
import { AttachFile } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useDispatch } from 'react-redux';
import { openDialog } from '@/features/dialog/dialogSlice';
import { AddFileDialog } from '../dialogs/AddFileDialog';
import { expandEmailFiles } from '@/utils/emailFileAttachments';
import { useAutomationContext } from '@/context/automationContext';
import { AUTOMATION_NAMES } from '@/utils/automationCatalog';

const LoadingBox = () => {
	const theme = useTheme();
	return (
		<Box
			width={'100%'}
			sx={{
				border: `2px dashed ${theme.palette.primary.main}`,
				borderRadius: '5px',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				backgroundColor: theme.palette.action.hover,
			}}
		>
			<Box sx={{ padding: '0.5em' }}>
				<CircularProgress />
			</Box>
		</Box>
	);
};

const FileUpload = ({
	files,
	setFiles,
	loading,
	types,
	multiple,
	attachFromSharepoint = false,
}) => {
	const theme = useTheme();
	const [dragging, setDragging] = useState(false);
	const [expanding, setExpanding] = useState(false);
	const dispatch = useDispatch();
	const isBusy = loading || expanding;
	const { getAutomations } = useAutomationContext();
	const extractEmailAttachments = Boolean(
		getAutomations(AUTOMATION_NAMES.autoEmailAttachments)?.enabled,
	);

	const handleDragAndDrop = async (file) => {
		const fileList = [];
		const items = Array.isArray(file) ? file : [file];

		for (const item of items) {
			if (item instanceof FileList) {
				fileList.push(...Array.from(item));
			} else if (item instanceof File) {
				fileList.push(item);
			}
		}

		setExpanding(true);
		try {
			const processedFiles = extractEmailAttachments
				? await expandEmailFiles(fileList, files || [])
				: fileList;

			let newFiles;
			if (multiple !== false) {
				newFiles = [...(files || []), ...processedFiles];
			} else {
				newFiles = processedFiles.slice(0, 1);
			}

			await Promise.resolve(setFiles(newFiles));
		} finally {
			setExpanding(false);
		}
	};

	const removeFile = (index) => {
		const newFiles = (files || []).filter((_, i) => i !== index);
		setFiles(newFiles);
		console.log('📋 ~ Selected files after delete', newFiles);
	};

	const handleSharePointAttach = () => {
		dispatch(
			openDialog({
				dialogName: 'selectFiles',
				data: {
					multiple: multiple !== false,
					...(Array.isArray(types) && types.length > 0 ? { types } : {}),
				},
			}),
		);
	};

	const hoverStyles = {
		color: theme.palette.mode === 'dark' ? theme.palette.folioWhite : theme.palette.primary.main,
		backgroundColor: theme.palette.mode === 'dark' ? theme.palette.darkModeSideNav : '#D9DCFE',
		borderRadius: '4px',
		alignItems: 'center',
		border: theme.palette.mode === 'dark' ? '1.5px solid #7B83FD' : '2px solid #7B83FD',
	};

	const getDisplayName = (file) => {
		return file.name || file.fileName || file.originalName;
	};

	const handlePreview = (file, e) => {
		e.stopPropagation();

		const fileName = getDisplayName(file);
		const extension = fileName.split('.').pop()?.toLowerCase();
		const nonPreviewableTypes = ['msg', 'eml'];

		if (nonPreviewableTypes.includes(extension)) {
			if (file instanceof File) {
				const blobUrl = URL.createObjectURL(file);
				const link = document.createElement('a');
				link.href = blobUrl;
				link.download = fileName;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
			}
			return;
		}
		if (file.url || file.downloadUrl || file.webUrl) {
			window.open(file.url || file.downloadUrl || file.webUrl, '_blank');
			return;
		}
		if (file instanceof File) {
			const blobUrl = URL.createObjectURL(file);
			window.open(blobUrl, '_blank');
			setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
		}
	};

	return (
		<>
			<Box
				sx={{
					'& .drop_zone': {
						width: '100%',
						display: 'flex',
						flexDirection: 'column',
					},
					width: '100%',
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				{attachFromSharepoint && (
					<Box display='flex' justifyContent='flex-end'>
						<Button
							variant='text'
							size='small'
							sx={{ textTransform: 'none' }}
							onClick={handleSharePointAttach}
						>
							Attach from Folio
						</Button>
					</Box>
				)}

				{isBusy ? (
					<LoadingBox />
				) : (
					<FileUploader
						classes='drop_zone'
						handleChange={handleDragAndDrop}
						multiple={multiple !== false}
						name='file'
						types={types}
						hoverTitle='Drop file here'
						dropMessageStyle={{
							width: '100%',
							height: '100%',
							...hoverStyles,
						}}
						onDraggingStateChange={(isDragging) => setDragging(isDragging)}
					>
						<div
							style={{
								border: `2px dashed ${theme.palette.primary.main}`,
								backgroundColor: theme.palette.background.paper,
								borderRadius: '5px',
								display: 'flex',
								alignItems: 'center',
								padding: '1em',
								cursor: 'pointer',
							}}
						>
							<UploadFileIcon
								sx={{
									fontSize: '2em',
									color: theme.palette.primary.main,
									marginRight: '0.25em',
								}}
							/>
							<Typography
								variant='card_header'
								sx={{
									color:
										theme.palette.mode === 'dark'
											? theme.palette.folioWhite
											: theme.palette.folioRegular,
								}}
							>
								{!dragging && (
									<>
										<span style={{ textDecoration: 'underline' }}>
											{'Upload'}
										</span>
										{' or drop a file right here'}
									</>
								)}
							</Typography>
						</div>
					</FileUploader>
				)}
				{files && files.length > 0 && (
					<List dense>
						{files.map((file, i) => (
							<ListItem
								disablePadding
								key={`${file.name || file.originalName || file.id}-${i}`}
								secondaryAction={
									<Box sx={{ display: 'flex', gap: 0.5 }}>
										<IconButton
											disabled={isBusy}
											edge='end'
											aria-label='delete'
											onClick={() => removeFile(i)}
										>
											<DeleteIcon />
										</IconButton>
									</Box>
								}
							>
								<ListItemIcon>
									<AttachFile />
								</ListItemIcon>
								<ListItemText
									sx={{
										color: theme.palette.primary.main,
										cursor: 'pointer',
									}}
									onClick={(e) => handlePreview(file, e)}
									primary={getDisplayName(file)}
								/>
							</ListItem>
						))}
					</List>
				)}
			</Box>
			{attachFromSharepoint && <AddFileDialog />}
		</>
	);
};

export default FileUpload;
